import {
    Box,
    Divider,
    Typography,
} from '@mui/material';
import React from 'react';
import { TrackedTextField } from 'src/theme/StyledFields';
import { useLessonPlanEditor } from '../../context/LessonPlanEditorContext';
import { LessonPlanSessionModel } from '../../object-actions/types/types';
import { LessonPlanMilestoneActions } from './LessonPlanMilestoneNav';
import LessonPlanSlideDeck from './LessonPlanSlideDeck';

type LessonPlanRole = 'payer' | 'athlete' | 'coach' | 'parent';

interface LessonPlanCardProps {
    session: LessonPlanSessionModel;
    lessonPlanId?: number | null;
    sessions: LessonPlanSessionModel[];
    paymentAssignmentId: number | null;
    onGrowthMessage: (message: string, severity: 'success' | 'error') => void;
    myRoles?: LessonPlanRole[];
    isPublished?: boolean;
    hideSessionTitle?: boolean;
    hasHumanCoach?: boolean;
}

const LessonPlanCard: React.FC<LessonPlanCardProps> = ({
    session,
    lessonPlanId,
    sessions,
    paymentAssignmentId,
    onGrowthMessage,
    myRoles,
    isPublished = false,
    hideSessionTitle = false,
    hasHumanCoach = false,
}) => {
    const { isEditMode, updateSession, updateSlide, getSessionWithEdits } = useLessonPlanEditor();

    // Get merged session data (original + edits) for previewing
    const sessionData = getSessionWithEdits(session);
    const headerText = sessionData.topic || `Session ${sessionData.session_number ?? session.id ?? ''}`;
    const milestoneWeek = [4, 8, 12].includes(sessionData.session_number)
        ? sessionData.session_number as 4 | 8 | 12
        : null;

    return (
        <Box
            component="article"
            data-attr="lesson-plan-card"
            data-custom-id={String(session.id)}
            data-testid={`lesson-plan-card--session-${session.session_number ?? session.id}`}
        >
            <Box
                component="header"
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 2,
                    mb: 2,
                    pb: 2,
                    borderBottom: theme => `1px solid ${theme.palette.divider}`,
                }}
            >
                <Box sx={{ flex: 1 }}>
                    {isEditMode ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <TrackedTextField
                                fullWidth
                                size="small"
                                label="Topic"
                                value={sessionData.topic || ''}
                                onChange={(e) => updateSession(session.id, { topic: e.target.value })}
                                onClick={(e) => e.stopPropagation()}
                                onFocus={(e) => e.stopPropagation()}
                                variant="outlined"
                                data-attr="lesson-plan-card_field-topic"
                                data-testid="lesson-plan-card_field-topic"
                            />
                            <TrackedTextField
                                fullWidth
                                size="small"
                                label="Objective"
                                value={sessionData.objective || ''}
                                onChange={(e) => updateSession(session.id, { objective: e.target.value })}
                                onClick={(e) => e.stopPropagation()}
                                onFocus={(e) => e.stopPropagation()}
                                variant="outlined"
                                multiline
                                rows={2}
                                data-attr="lesson-plan-card_field-objective"
                                data-testid="lesson-plan-card_field-objective"
                            />
                            {sessionData.theme && (
                                <TrackedTextField
                                    fullWidth
                                    size="small"
                                    label="Theme"
                                    value={sessionData.theme || ''}
                                    onChange={(e) => updateSession(session.id, { theme: e.target.value })}
                                    onClick={(e) => e.stopPropagation()}
                                    onFocus={(e) => e.stopPropagation()}
                                    variant="outlined"
                                    data-attr="lesson-plan-card_field-theme"
                                    data-testid="lesson-plan-card_field-theme"
                                />
                            )}
                            {sessionData.key_tools && (
                                <TrackedTextField
                                    fullWidth
                                    size="small"
                                    label="Key Tools"
                                    value={sessionData.key_tools || ''}
                                    onChange={(e) => updateSession(session.id, { key_tools: e.target.value })}
                                    onClick={(e) => e.stopPropagation()}
                                    onFocus={(e) => e.stopPropagation()}
                                    variant="outlined"
                                    multiline
                                    rows={2}
                                    data-attr="lesson-plan-card_field-key-tools"
                                    data-testid="lesson-plan-card_field-key-tools"
                                />
                            )}
                        </Box>
                    ) : (
                        <>
                            {!hideSessionTitle && (
                                <Typography
                                    data-testid="lesson-plan-card_header-text"
                                    variant="h6"
                                    sx={{
                                        fontWeight: 'bold',
                                        color: 'text.primary',
                                    }}
                                >
                                    {headerText}
                                </Typography>
                            )}
                        </>
                    )}
                </Box>
            </Box>

            <LessonPlanSlideDeck
                session={sessionData}
                lessonPlanId={lessonPlanId}
                isExpanded
                myRoles={myRoles}
                isPublished={isPublished}
                hasHumanCoach={hasHumanCoach}
                updateSlide={updateSlide}
                updateSession={updateSession}
            />

            {milestoneWeek != null && lessonPlanId != null && (
                <Box
                    component="footer"
                    data-testid={`lesson-plan-session-milestone--week-${milestoneWeek}`}
                    sx={{ mt: 2, pt: 2, pb: 1 }}
                >
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="h6" component="h3" sx={{ mb: 0.5, fontWeight: 700 }}>
                        {milestoneWeek === 12 ? 'Final progress checkpoint' : `Week ${milestoneWeek} progress checkpoint`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Complete the progress survey, then generate the growth report for this checkpoint.
                    </Typography>
                    <LessonPlanMilestoneActions
                        week={milestoneWeek}
                        lessonPlanId={lessonPlanId}
                        sessions={sessions}
                        myRoles={myRoles}
                        paymentAssignmentId={paymentAssignmentId}
                        onMessage={onGrowthMessage}
                    />
                </Box>
            )}
        </Box>
    );
};

export default LessonPlanCard;
